import React, { useEffect, useState } from 'react'
import { clearAlert, useDispatch } from '@ggs/store'
import { useI18n } from '@ggs/i18n'

import { Button, Dialog } from '@mui/material'
import {
  CheckCircle,
  Error,
  Help,
  Info,
  WarningAmber,
} from '@mui/icons-material'

import './SnackbarDialog.scss'


/**
 * @typedef {import('@ggs/store').AlertMessage} AlertMessage
 * @typedef {import('@mui/material').AlertColor} AlertColor
 * @typedef {import("@mui/material/SvgIcon").SvgIconProps} IconProps
 */

/**
 * @typedef {Object} SnackbarAlertProps
 * @property {AlertMessage} alert
 * @property {AlertColor} severity
 */

/**
 * Snackbar notification alert item as a dialog.
 * @param {SnackbarAlertProps} props
 * @return {JSX.Element}
 */
const SnackbarDialog = ({ alert, severity }) => {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const autoHideDuration = 12000

  const [open, setOpen] = useState(true)

  useEffect(() => {
    setTimeout(() => {
      // Auto close after 6 seconds.
      dispatch(clearAlert(alert.id))
      setOpen(false)
    }, autoHideDuration)
  }, [alert.id])

  /**
   * Handle single alert close.
   * @param {React.SyntheticEvent<any>} event User interaction event
   */
  const handleAlertClose = (event) => {
    dispatch(clearAlert(alert.id))
    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      onClose={handleAlertClose}
      classes={{
        root: 'snackbar-dialog',
        container: 'snackbar-dialog__container',
        paper: 'snackbar-dialog__paper'
      }}
      PaperProps={{
        elevation: 0
      }}
    >
      <AlertIcon type={severity} />
      <div className="snackbar-dialog__title">{alert.title}</div>
      <Button
        disableElevation
        variant="contained"
        onClick={handleAlertClose}
        aria-label={t('global:aria.label.closeThisNotice')}
        classes={{
          root: 'snackbar-dialog__btn'
        }}
      >
        {t('Done')}
      </Button>
    </Dialog>
  )
}

export default SnackbarDialog

/**
 * @typedef {Object} AlertIconProps
 * @property {AlertColor} type
 */

/**
 * Create an Alert Icon component based on the severity type
 * @param {AlertIconProps} props
 * @return {JSX.Element}
 */
const AlertIcon = ({ type }) => {

  /** @type {IconProps} */
  const props = {
    color: type,
    classes: {
      root: 'snackbar-dialog__icon'
    },
    fontSize: 'large'
  }

  switch (type) {
    case 'error': return <Error {...props} />
    case 'warning': return <WarningAmber {...props} />
    case 'success': return <CheckCircle {...props} />
    case 'info': return <Info {...props} />
    default: return <Help {...props} />
  }
}
